import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { YT_STATE } from '../../utils/music-request';
import { normalizeVolume } from '../../utils/overlay-volume';
import { loadYouTubeIframeApi, YTPlayer } from './youtube-iframe-api';

interface Options {
  /** false ⇒ không tạo player (vd overlay sai key); đổi sang false thì player bị huỷ. */
  enabled: boolean;
  onStateChange?: (state: number) => void;
  onError?: (code: number) => void;
  /** Gọi mỗi 500ms khi đang giữ một video: thời điểm đang phát + trạng thái player. */
  onTick?: (currentTime: number, state: number) => void;
  /** Âm lượng 0–100 streamer đặt; thiếu ⇒ 100. Áp khi player sẵn sàng, mỗi lần nạp bài và mỗi lần số này đổi. */
  volume?: number;
}

// Trình duyệt thường chặn tự phát có tiếng: sau ngần này mà video chưa chạy thì tắt tiếng rồi phát.
const AUTOPLAY_CHECK_MS = 2000;
const AUTOPLAY_RETRY_MS = 1500;
const TICK_MS = 500;

interface PendingLoad {
  videoId: string;
  startSeconds: number;
  paused: boolean;
}

/**
 * Bọc YouTube IFrame Player cho overlay OBS: nạp API một lần, tạo `YT.Player` trong `hostRef`
 * (player tạo vào một div con dựng tay, không đụng cây DOM do React quản lý), đọc thời gian
 * mỗi 500ms, tự tắt tiếng khi trình duyệt chặn tự phát có tiếng. Dọn player và mọi bộ đếm
 * khi unmount hoặc khi `enabled` về false.
 */
const useYoutubePlayer = (hostRef: RefObject<HTMLDivElement>, options: Options) => {
  const { enabled } = options;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const volume = normalizeVolume(options.volume);
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  const [ready, setReady] = useState(false);
  const [state, setState] = useState<number>(YT_STATE.UNSTARTED);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadedVideoId, setLoadedVideoId] = useState<string | null>(null);
  const [needsUnmute, setNeedsUnmute] = useState(false);

  const playerRef = useRef<YTPlayer | null>(null);
  const readyRef = useRef(false);
  const pendingRef = useRef<PendingLoad | null>(null);
  const wantPlayingRef = useRef(false);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // setVolume không bật lại tiếng khi player đang bị tắt tiếng (trình duyệt chặn tự phát), nút bật tiếng vẫn cần thiết.
  const applyVolume = useCallback((p: YTPlayer | null) => {
    if (!p) return;
    try {
      p.setVolume(volumeRef.current);
    } catch {
      // Player chưa sẵn sàng hoặc đã bị huỷ.
    }
  }, []);

  const clearAutoplayTimer = useCallback(() => {
    if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
    autoplayTimerRef.current = null;
  }, []);

  const fallbackMuted = useCallback(() => {
    const p = playerRef.current;
    if (!p || !wantPlayingRef.current) return;
    try {
      p.mute();
      p.playVideo();
      setNeedsUnmute(true);
    } catch {
      // Player đã bị huỷ.
    }
  }, []);

  // Hai bước để không tắt tiếng oan khi chỉ là mạng chậm (quan trọng với OBS, nơi không ai bấm
  // được nút bật tiếng): chưa chạy thì thử phát lại có tiếng, vẫn chưa chạy mới tắt tiếng.
  const armAutoplayCheck = useCallback(() => {
    clearAutoplayTimer();
    if (!wantPlayingRef.current) return;
    const notRunning = (p: YTPlayer) => {
      const s = p.getPlayerState();
      return s !== YT_STATE.PLAYING && s !== YT_STATE.BUFFERING && s !== YT_STATE.ENDED;
    };
    autoplayTimerRef.current = setTimeout(() => {
      autoplayTimerRef.current = null;
      const p = playerRef.current;
      if (!p || !wantPlayingRef.current) return;
      try {
        if (!notRunning(p)) return;
        p.playVideo();
      } catch {
        return;
      }
      autoplayTimerRef.current = setTimeout(() => {
        autoplayTimerRef.current = null;
        const p2 = playerRef.current;
        if (!p2 || !wantPlayingRef.current) return;
        try {
          if (notRunning(p2)) fallbackMuted();
        } catch {
          // Player đã bị huỷ.
        }
      }, AUTOPLAY_RETRY_MS);
    }, AUTOPLAY_CHECK_MS);
  }, [fallbackMuted, clearAutoplayTimer]);

  const doLoad = useCallback(
    (p: YTPlayer, req: PendingLoad) => {
      applyVolume(p);
      if (req.paused) p.cueVideoById({ videoId: req.videoId, startSeconds: req.startSeconds });
      else p.loadVideoById({ videoId: req.videoId, startSeconds: req.startSeconds });
      armAutoplayCheck();
    },
    [armAutoplayCheck, applyVolume]
  );

  // Tạo / huỷ player.
  useEffect(() => {
    const host = hostRef.current;
    if (!enabled || !host) return undefined;
    let cancelled = false;
    const mountEl = document.createElement('div');
    host.appendChild(mountEl);

    loadYouTubeIframeApi()
      .then((YT) => {
        if (cancelled) return;
        playerRef.current = new YT.Player(mountEl, {
          width: '100%',
          height: '100%',
          playerVars: { autoplay: 1, controls: 0, rel: 0, modestbranding: 1, playsinline: 1 },
          events: {
            onReady: (e) => {
              if (cancelled) return;
              playerRef.current = e.target;
              readyRef.current = true;
              applyVolume(e.target);
              setReady(true);
              const pending = pendingRef.current;
              pendingRef.current = null;
              if (pending) doLoad(e.target, pending);
            },
            onStateChange: (e) => {
              if (cancelled) return;
              setState(e.data);
              if (e.data === YT_STATE.PLAYING) {
                clearAutoplayTimer();
                // Bài mới bắt đầu: áp lại cho chắc, trình nhúng có thể nhớ âm lượng cũ của lần xem trước.
                applyVolume(e.target);
                try {
                  // Một số trình duyệt cho tự phát nhưng ép tắt tiếng.
                  if (e.target.isMuted()) setNeedsUnmute(true);
                } catch {
                  // bỏ qua
                }
              }
              optionsRef.current.onStateChange?.(e.data);
            },
            onError: (e) => {
              if (cancelled) return;
              clearAutoplayTimer();
              optionsRef.current.onError?.(e.data);
            },
            onAutoplayBlocked: () => {
              if (!cancelled) fallbackMuted();
            },
          },
        });
      })
      .catch(() => {
        // Không tải được API YouTube: overlay giữ trống, lần mở lại trang sẽ thử lại.
      });

    return () => {
      cancelled = true;
      clearAutoplayTimer();
      try {
        playerRef.current?.destroy();
      } catch {
        // Player chưa dựng xong.
      }
      playerRef.current = null;
      readyRef.current = false;
      pendingRef.current = null;
      wantPlayingRef.current = false;
      // Div con (hoặc iframe thay thế nó) do hook tự dựng, React không quản lý nên xoá tay.
      while (host.firstChild) host.removeChild(host.firstChild);
      setReady(false);
      setLoadedVideoId(null);
      setState(YT_STATE.UNSTARTED);
      setCurrentTime(0);
      setDuration(0);
      setNeedsUnmute(false);
    };
  }, [enabled, hostRef, doLoad, fallbackMuted, clearAutoplayTimer, applyVolume]);

  // Streamer đổi âm lượng giữa chừng: áp ngay cho bài đang phát, không nạp lại bài.
  useEffect(() => {
    if (ready) applyVolume(playerRef.current);
  }, [ready, volume, applyVolume]);

  // Đọc thời gian mỗi 500ms khi đang giữ một video.
  useEffect(() => {
    if (!ready || !loadedVideoId) return undefined;
    const timer = setInterval(() => {
      const p = playerRef.current;
      if (!p) return;
      let t = 0;
      let s: number = YT_STATE.UNSTARTED;
      let d = 0;
      try {
        t = p.getCurrentTime() || 0;
        s = p.getPlayerState();
        d = p.getDuration() || 0;
      } catch {
        return;
      }
      setCurrentTime(t);
      setDuration(d);
      optionsRef.current.onTick?.(t, s);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [ready, loadedVideoId]);

  const play = useCallback(
    (videoId: string, startSeconds = 0, paused = false) => {
      wantPlayingRef.current = !paused;
      setLoadedVideoId(videoId);
      setCurrentTime(startSeconds);
      setDuration(0);
      const req = { videoId, startSeconds, paused };
      const p = playerRef.current;
      if (!p || !readyRef.current) {
        pendingRef.current = req;
        return;
      }
      try {
        doLoad(p, req);
      } catch {
        pendingRef.current = req;
      }
    },
    [doLoad]
  );

  const pause = useCallback(() => {
    wantPlayingRef.current = false;
    clearAutoplayTimer();
    if (pendingRef.current) pendingRef.current.paused = true;
    try {
      playerRef.current?.pauseVideo();
    } catch {
      // bỏ qua
    }
  }, [clearAutoplayTimer]);

  const resume = useCallback(() => {
    wantPlayingRef.current = true;
    if (pendingRef.current) {
      pendingRef.current.paused = false;
      return;
    }
    try {
      playerRef.current?.playVideo();
      armAutoplayCheck();
    } catch {
      // bỏ qua
    }
  }, [armAutoplayCheck]);

  const stop = useCallback(() => {
    wantPlayingRef.current = false;
    pendingRef.current = null;
    clearAutoplayTimer();
    try {
      playerRef.current?.stopVideo();
    } catch {
      // bỏ qua
    }
    setLoadedVideoId(null);
    setCurrentTime(0);
    setDuration(0);
  }, [clearAutoplayTimer]);

  const unMute = useCallback(() => {
    try {
      playerRef.current?.unMute();
      playerRef.current?.setVolume(volumeRef.current);
    } catch {
      // bỏ qua
    }
    setNeedsUnmute(false);
  }, []);

  return {
    ready,
    state,
    currentTime,
    duration,
    loadedVideoId,
    needsUnmute,
    play,
    pause,
    resume,
    stop,
    unMute,
  };
};

export default useYoutubePlayer;
